/**
 * Deep-link readers: the camera (`#lat=&lng=&zoom=`, and `#hist=1` on its
 * own), the tab (`#layer=`, `#walk=`) and the timeline's year (`#year=`).
 * App.tsx reads all three at first render,
 * for `initialViewState` and the initial tab state, and again in the onLoad
 * handler, so nothing fetches for a view or a tab nobody linked to. No clamp:
 * MapLibre constrains the camera. The one ceiling that is ours, 13 on the
 * 1842-1899 survey sheets, is applied here, and the sheets' home view is
 * exported for the toggle in App.tsx that needs the same camera.
 */
import type { ActiveTab, CategoryGroup } from "../types/index.ts";
import { LAYER_GROUP } from "../constants/index.ts";
export interface DeepLinkTarget {
  latitude: number;
  longitude: number;
  zoom: number;
}

/**
 * Where a survey-sheet link lands without a coordinate of its own: the
 * sheets cover Denmark and nothing else, and a geo-IP answer abroad would
 * open onto bare paper.
 */
export const SURVEY_HOME: DeepLinkTarget = { longitude: 10.2, latitude: 56.2, zoom: 7.5 };

/**
 * The archive tops out at z13. Flying to 15 stretched the 1:20,000 scans
 * fourfold into mush, measured on a phone, and an explicit `zoom=` above it
 * would do the same eightfold, so the ceiling beats the link.
 */
export const SURVEY_MAX_ZOOM = 13;

/**
 * The coordinate a shared `/s/` page carries in its own JSON-LD.
 *
 * Sitemap URLs stopped carrying `lat` and `lng` in May 2026, deliberately:
 * they are not part of a site's identity, and emitting them while api/og.ts
 * canonicalised without them produced about 35 000 "Alternate page with proper
 * canonical tag" entries in Search Console. What nobody measured is what it
 * cost the link. Without a coordinate the map opens on the stored view, the
 * layer fetches for *that* viewport, and the pin the link names is never in
 * the index — so the panel never opens.
 *
 * Measured on 18 Sep 2026 against one URL per prefix in the sitemap: 13 of 41
 * prefixes, 30 302 URLs, opened an empty map. Among them every OSM monument
 * (13 262), every folklore record (10 762) and the Estonian sacred trees.
 *
 * The server already knows. api/og.ts looks the coordinate up in
 * site-lookup.json and prints it as JSON-LD GeoCoordinates for the crawler, so
 * the page in front of the reader carries the answer. Reading it here gives
 * the map a camera without putting anything back in the URL.
 */
export function coordinateFromPageMetadata(doc: Pick<Document, "querySelectorAll">): { latitude: number; longitude: number } | null {
  for (const el of doc.querySelectorAll('script[type="application/ld+json"]')) {
    try {
      const geo = findGeo(JSON.parse(el.textContent ?? ""));
      if (geo) return geo;
    } catch { /* a malformed block is not a reason to fail the load */ }
  }
  return null;
}

/** GeoCoordinates can sit at any depth of the graph, so walk it. */
function findGeo(node: unknown): { latitude: number; longitude: number } | null {
  if (!node || typeof node !== "object") return null;
  if (Array.isArray(node)) {
    for (const item of node) { const hit = findGeo(item); if (hit) return hit; }
    return null;
  }
  const o = node as Record<string, unknown>;
  const lat = Number(o.latitude), lng = Number(o.longitude);
  if (isFinite(lat) && isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
    return { latitude: lat, longitude: lng };
  }
  for (const v of Object.values(o)) { const hit = findGeo(v); if (hit) return hit; }
  return null;
}

/** Null when the hash carries no view at all, so the stored view applies. */
export function parseDeepLinkTarget(hash: string): DeepLinkTarget | null {
  const p = new URLSearchParams(hash.slice(1));
  const hist = p.get("hist") === "1";
  const latitude = parseFloat(p.get("lat") ?? "");
  const longitude = parseFloat(p.get("lng") ?? "");
  const z = parseFloat(p.get("zoom") ?? "");
  const onGlobe = isFinite(latitude) && isFinite(longitude)
    && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180;
  if (!onGlobe) {
    if (!hist) return null;
    return isFinite(z) ? { ...SURVEY_HOME, zoom: Math.min(z, SURVEY_MAX_ZOOM) } : SURVEY_HOME;
  }
  const fallback = hist ? SURVEY_MAX_ZOOM : p.get("site") ? 16 : 15;
  const zoom = isFinite(z) ? z : fallback;
  return { latitude, longitude, zoom: hist ? Math.min(zoom, SURVEY_MAX_ZOOM) : zoom };
}

/**
 * The tab a deep link names, or null. `walk=` is the walk tab; `layer=` is
 * the layer's own tab and its group from GROUP_TABS (`building` is the old
 * name of `monument`). A layer in no group is no tab, and the caller keeps
 * the stored one. Read at first render, because an effect gated on the
 * initial tab can fire before the map's load event runs the handler: the
 * Wikidata wreck file landed first and fetched two countries' wreck
 * registers for a link that named the folklore tab, measured 2 Sep 2026.
 */
export function parseDeepLinkTab(hash: string): { group: CategoryGroup; tab: ActiveTab } | null {
  const p = new URLSearchParams(hash.slice(1));
  if (p.get("walk")) return { group: "explore", tab: "walk" };
  const layer = p.get("layer");
  if (!layer) return null;
  const tab = (layer === "building" ? "monument" : layer) as ActiveTab;
  const group = LAYER_GROUP[tab];
  return group ? { group, tab } : null;
}

/** Years the timeline can show: the earliest Nordic Stone Age to now. */
export const TIMELINE_MIN_YEAR = -10000;
export const TIMELINE_MAX_YEAR = new Date().getFullYear();

/**
 * The timeline's year from `year=`, or null when the link names none or names
 * nonsense. Negative is BC, so `year=-2800` is Orkney before Brodgar. Clamped
 * to the bar's own range rather than rejected, because a link is a promise
 * the reader should land near.
 */
export function parseDeepLinkYear(hash: string): number | null {
  const raw = new URLSearchParams(hash.slice(1)).get("year");
  if (raw == null || !/^-?\d{1,6}$/.test(raw.trim())) return null;
  const y = parseInt(raw, 10);
  return Math.max(TIMELINE_MIN_YEAR, Math.min(TIMELINE_MAX_YEAR, y));
}

/**
 * `unbuilt=0` hides the timeline's hollow rings for sites not yet built, so a
 * link can open onto only what stood at the year. Anything else, including no
 * parameter, keeps them: the rings are the default and the link opts out.
 */
export function parseDeepLinkShowUnbuilt(hash: string): boolean {
  return new URLSearchParams(hash.slice(1)).get("unbuilt") !== "0";
}
